import { useState, useMemo, useEffect } from 'react'
import { usePlayerStore, useAuthStore } from '../stores'
import { MatchStats, MatchRecord } from '../types'
import { Save, X, Plus, Trophy, MapPin, Users } from 'lucide-react'
import {
  APPROVED_COMPETITION_EXAMPLES,
  COMPETITION_POLICY_NOTICE,
  collectCompetitionNames,
  getCompetitionPolicyViolation,
  inferCompetitionId,
} from '../utils/competitions'

const INITIAL_STATS: MatchStats = {
  goals: 0,
  assists: 0,
  key_passes: 0,
  dribbles: 0,
  tackles: 0,
  clearances: 0,
  interceptions: 0,
  yellow_cards: 0,
  red_cards: 0,
  shots_on_target: 0,
  shots_off_target: 0,
  gk_saves: 0,
  aeriel_duels: 0,
  ground_duels: 0,
  forward_pass: 0,
  back_pass: 0,
  touches: 0,
  mins: 0,
  take_ons: 0,
  touches_in_opposition_half: 0,
  clean_sheets: 0,
  fouls: 0,
  chance_created: 0,
}

const STAT_CONFIG: { key: keyof MatchStats; label: string; color: string }[] = [
  { key: 'goals', label: 'Goals', color: 'bg-green-600' },
  { key: 'assists', label: 'Assists', color: 'bg-blue-600' },
  { key: 'key_passes', label: 'Key Passes', color: 'bg-indigo-600' },
  { key: 'shots_on_target', label: 'Shots (On)', color: 'bg-emerald-600' },
  { key: 'shots_off_target', label: 'Shots (Off)', color: 'bg-gray-600' },
  { key: 'dribbles', label: 'Dribbles', color: 'bg-orange-600' },
  { key: 'tackles', label: 'Tackles', color: 'bg-red-600' },
  { key: 'interceptions', label: 'Interceptions', color: 'bg-purple-600' },
  { key: 'clearances', label: 'Clearances', color: 'bg-yellow-700' },
  { key: 'gk_saves', label: 'GK Saves', color: 'bg-cyan-600' },
  { key: 'yellow_cards', label: 'Yellow Card', color: 'bg-yellow-500' },
  { key: 'red_cards', label: 'Red Card', color: 'bg-red-700' },
  { key: 'aeriel_duels', label: 'Aeriel Duels', color: 'bg-blue-900' },
  { key: 'ground_duels', label: 'Ground Duels', color: 'bg-green-900' },
  { key: 'forward_pass', label: 'Forward Pass', color: 'bg-cyan-900' },
  { key: 'back_pass', label: 'Back Pass', color: 'bg-gray-900' },
  { key: 'touches', label: 'Touches', color: 'bg-yellow-900' },
  { key: 'mins', label: 'Minutes Played', color: 'bg-lime-700' },
  { key: 'take_ons', label: 'Take Ons', color: 'bg-pink-700' },
  { key: 'touches_in_opposition_half', label: 'Touches in Opp. Half', color: 'bg-fuchsia-700' },
  { key: 'clean_sheets', label: 'Clean Sheets', color: 'bg-green-700' },
  { key: 'fouls', label: 'Fouls', color: 'bg-red-900' },
  { key: 'chance_created', label: 'Chance Created', color: 'bg-indigo-900' },
]

function MatchRecorder() {
  const { players, clubs, competitions, addMatchRecord, fetchPlayers } = usePlayerStore()
  const { user } = useAuthStore()
  const [selectedPlayerId, setSelectedPlayerId] = useState('')
  const [isRecording, setIsRecording] = useState(false)
  const [matchInfo, setMatchInfo] = useState({ opponent: '', location: '', competition: '' })
  const [currentStats, setCurrentStats] = useState<MatchStats>(INITIAL_STATS)
  const [notes, setNotes] = useState('')
  const [success, setSuccess] = useState('')
  const [error, setError] = useState('')
  const [customCompetitionName, setCustomCompetitionName] = useState('')

  const selectedPlayer = useMemo(() => 
    players.find(p => p.id === selectedPlayerId), 
  [players, selectedPlayerId])

  const competitionOptions = useMemo(() => {
    return collectCompetitionNames({ competitions, clubs, player: selectedPlayer })
  }, [clubs, competitions, selectedPlayer])

  useEffect(() => {
    if (players.length === 0) {
      void fetchPlayers()
    }
  }, [fetchPlayers, players.length])

  useEffect(() => {
    if (!selectedPlayer) return
    setMatchInfo((prev) => {
      if (prev.competition?.trim()) return prev
      return {
        ...prev,
        competition: selectedPlayer.competitions?.[0] || selectedPlayer.league || competitionOptions[0] || '',
      }
    })
  }, [competitionOptions, selectedPlayer])

  const availablePlayers = useMemo(() => {
    if (user?.role === 'admin') return players
    if (user?.role === 'club') return players.filter(p => p.owner_club_id === user.id)
    if (!user?.email) return []
    return players.filter(p => p.scout === user.email)
  }, [players, user?.email, user?.role, user?.id])

  useEffect(() => {
    if (selectedPlayerId && !availablePlayers.some(p => p.id === selectedPlayerId)) {
      setSelectedPlayerId('')
    }
  }, [availablePlayers, selectedPlayerId])

  const incrementStat = (key: keyof MatchStats) => {
    setCurrentStats(prev => ({
      ...prev,
      [key]: prev[key] + 1
    }))
  }

  const decrementStat = (key: keyof MatchStats) => {
    setCurrentStats(prev => ({
      ...prev,
      [key]: Math.max(0, prev[key] - 1)
    }))
  }

  const handleStart = () => {
    if (!selectedPlayerId) return
    setIsRecording(true)
  }

  const handleSave = () => {
    if (!selectedPlayerId) return
    const competitionName = matchInfo.competition || customCompetitionName || selectedPlayer?.competitions?.[0] || selectedPlayer?.league || ''
    const competitionViolation = getCompetitionPolicyViolation(competitionName)
    if (competitionViolation) {
      setError(competitionViolation)
      return
    }
    
    const record: MatchRecord = {
      id: `match_${Date.now()}`,
      date: new Date().toISOString(),
      opponent: matchInfo.opponent || 'Unknown Opponent',
      location: matchInfo.location || 'Unknown Location',
      competition_id: inferCompetitionId(competitionName, competitions),
      competition: competitionName || 'Unknown Competition',
      stats: currentStats,
      notes: notes
    }

    addMatchRecord(selectedPlayerId, record)
    setError('')
    setSuccess('Match record saved successfully!')
    
    // Reset after delay
    setTimeout(() => {
      setIsRecording(false)
      setCurrentStats(INITIAL_STATS)
      setMatchInfo({ opponent: '', location: '', competition: '' })
      setNotes('')
      setCustomCompetitionName('')
      setSuccess('')
    }, 2000)
  }

  if (success) {
    return (
      <div className="max-w-2xl mx-auto flex flex-col items-center justify-center py-20 px-6 text-center animate-in fade-in zoom-in duration-500">
        <div className="w-20 h-20 bg-green-950/30 rounded-full flex items-center justify-center mb-6 border border-green-800/30">
          <Save className="text-green-500 w-10 h-10" />
        </div>
        <h2 className="text-3xl font-black text-white mb-2">{success}</h2>
        <p className="text-gray-500">Redirecting to session dashboard...</p>
      </div>
    )
  }

  if (!isRecording) {
    return (
      <div className="max-w-2xl mx-auto bg-[#161b22] border border-gray-800 shadow-2xl rounded-3xl overflow-hidden">
        <div className="bg-[#0b0f1a] p-8 text-white relative border-b border-gray-800">
          <div className="absolute top-0 right-0 p-8 opacity-5">
            <Trophy size={120} />
          </div>
          <h2 className="text-3xl font-black mb-2 uppercase tracking-tighter italic text-white">Live Match Recorder</h2>
          <p className="text-gray-500 text-sm font-medium">Select a player and initialize the session</p>
        </div>

        <div className="p-8 space-y-6">
          <div>
            <label className="flex text-xs font-black text-gray-500 uppercase tracking-widest mb-2 items-center gap-2">
              <Users size={14} className="text-green-500" />
              Target Player
            </label>
            <select
              value={selectedPlayerId}
              onChange={(e) => setSelectedPlayerId(e.target.value)}
              className="w-full px-6 py-4 bg-[#0d1117] border-2 border-gray-800 rounded-2xl text-white font-bold focus:ring-4 focus:ring-green-500/10 focus:border-green-500 outline-none transition appearance-none cursor-pointer"
            >
              <option value="">Choose a player...</option>
              {availablePlayers.map(p => (
                <option key={p.id} value={p.id}>{p.name} ({p.main_position})</option>
              ))}
            </select>
            <p className="text-xs text-gray-400 mt-2">
              {user?.role === 'admin'
                ? 'Admins see every player in the database.'
                : user?.role === 'club'
                  ? 'Clubs only see players in their active squad.'
                  : 'Scouts only see the players they logged.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="flex text-xs font-black text-gray-500 uppercase tracking-widest mb-2 items-center gap-2">
                <Users size={14} className="text-blue-500" />
                Opponent
              </label>
              <input
                type="text"
                placeholder="e.g., Shooting Stars"
                value={matchInfo.opponent}
                onChange={(e) => setMatchInfo(prev => ({ ...prev, opponent: e.target.value }))}
                className="w-full px-4 py-3 bg-[#0d1117] border border-gray-800 rounded-xl font-bold text-white placeholder-gray-600 focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition"
              />
            </div>
            <div>
              <label className="flex text-xs font-black text-gray-500 uppercase tracking-widest mb-2 items-center gap-2">
                <MapPin size={14} className="text-red-500" />
                Location
              </label>
              <input
                type="text"
                placeholder="e.g., Lekan Salami Stadium"
                value={matchInfo.location}
                onChange={(e) => setMatchInfo(prev => ({ ...prev, location: e.target.value }))}
                className="w-full px-4 py-3 bg-[#0d1117] border border-gray-800 rounded-xl font-bold text-white placeholder-gray-600 focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition"
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="flex text-xs font-black text-gray-500 uppercase tracking-widest mb-2 items-center gap-2">
              <Trophy size={14} className="text-yellow-400" />
              Competition / League
            </label>
            <select
              value={competitionOptions.includes(matchInfo.competition) ? matchInfo.competition : ''}
              onChange={(e) => {
                const value = e.target.value
                setMatchInfo((prev) => ({ ...prev, competition: value }))
                setCustomCompetitionName('')
              }}
              className="w-full px-4 py-3 bg-[#0d1117] border border-gray-800 rounded-xl font-bold text-white placeholder-gray-600 focus:ring-2 focus:ring-yellow-500 focus:border-transparent outline-none transition"
            >
              <option value="">Select competition</option>
              {competitionOptions.map((competition) => (
                <option key={competition} value={competition}>{competition}</option>
              ))}
            </select>
            <input
              type="text"
              value={customCompetitionName}
              onChange={(e) => {
                setCustomCompetitionName(e.target.value)
                setMatchInfo((prev) => ({ ...prev, competition: '' }))
              }}
              placeholder="Or type a recognized competition or tournament"
              className="w-full mt-3 px-4 py-3 bg-[#0d1117] border border-gray-800 rounded-xl font-bold text-white placeholder-gray-600 focus:ring-2 focus:ring-yellow-500 focus:border-transparent outline-none transition"
            />
            <p className="text-xs text-gray-400 mt-2">
              {COMPETITION_POLICY_NOTICE}
              {' '}
              Examples: {APPROVED_COMPETITION_EXAMPLES.join(', ')}.
            </p>
          </div>

          <button
            onClick={handleStart}
            disabled={!selectedPlayerId}
            className="w-full py-5 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:grayscale rounded-2xl font-black text-white text-lg shadow-xl shadow-green-600/20 transition-all hover:scale-[1.02] active:scale-[0.98] mt-4 flex items-center justify-center gap-2"
          >
            Start Recording Session
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-20">
      {error && (
        <div className="rounded-2xl border border-red-800/50 bg-red-950/30 px-5 py-4 text-red-200 text-sm font-semibold">
          {error}
        </div>
      )}
      {/* Session Header */}
      <div className="bg-[#161b22] rounded-3xl p-6 text-white flex items-center justify-between sticky top-20 z-20 shadow-2xl backdrop-blur-md border border-gray-800">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center font-black text-xl text-white shadow-lg shadow-green-600/20">
            {selectedPlayer?.name.charAt(0)}
          </div>
          <div>
            <h3 className="font-black text-xl italic tracking-tight uppercase text-white">{selectedPlayer?.name}</h3>
            <p className="text-xs text-green-500 font-bold uppercase tracking-wider">Live Stat Recording...</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={handleSave}
            className="px-6 py-3 bg-green-600 rounded-xl font-black text-sm uppercase flex items-center gap-2 hover:bg-green-700 transition"
          >
            <Save size={18} />
            Finish & Save
          </button>
          <button 
            onClick={() => setIsRecording(false)}
            className="p-3 bg-gray-800 border border-gray-700 hover:bg-red-900/50 rounded-xl text-gray-500 transition"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Grid of Stat Buttons */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {STAT_CONFIG.map(({ key, label, color }) => (
          <div key={key} className="bg-[#161b22] border border-gray-800 rounded-3xl p-4 shadow-sm hover:shadow-md transition">
            <div className="flex justify-between items-start mb-4">
              <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest">{label}</span>
              <button 
                onClick={() => decrementStat(key)}
                className="w-8 h-8 rounded-full bg-[#0d1117] border border-gray-800 flex items-center justify-center text-gray-500 hover:bg-red-900/20 hover:text-red-500 transition"
              >
                -
              </button>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-4xl font-black text-white min-w-[60px] text-center">{currentStats[key]}</span>
              <input
                type="number"
                value={currentStats[key]}
                onChange={(e) => {
                  const value = parseInt(e.target.value) || 0
                  setCurrentStats(prev => ({ ...prev, [key]: value }))
                }}
                className={`w-20 h-14 rounded-2xl ${color} text-white flex items-center justify-center shadow-lg transition-transform text-center font-bold text-lg outline-none border-2 border-transparent focus:border-white/30`}
                min="0"
                max="999"
              />
              <button
                onClick={() => incrementStat(key)}
                className={`w-14 h-14 rounded-2xl ${color} text-white flex items-center justify-center shadow-lg transition-transform active:scale-90`}
              >
                <Plus size={24} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Notes */}
      <div className="bg-[#161b22] border border-gray-800 rounded-3xl p-6 shadow-sm">
        <label className="block text-xs font-black text-gray-500 uppercase tracking-widest mb-4">Real-time Observations</label>
        <textarea
          placeholder="Add tactical notes as they happen..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={4}
          className="w-full bg-[#0d1117] border border-gray-800 rounded-2xl p-4 font-semibold text-white placeholder-gray-600 focus:ring-4 focus:ring-green-500/10 outline-none transition"
        />
      </div>
    </div>
  )
}

export default MatchRecorder
